import { useEffect, useState, type Dispatch, type SetStateAction } from "react";
import { CODIGO_A_NOMBRE_PRUEBA } from "../constants/pruebasMappings";
import type { Pedido } from "../types/pedido";

// --- PRECARGA AUTOMÁTICA DE PRUEBAS DESDE ARTICULOS ---
export function precargarPruebasDesdeArticulos(pedido: any): Array<{ nombre: string; ROW_ID: string; ACEPTADO: string; DOC?: string; RAZON_RECHAZO?: string }> {
  if (!pedido.articulos) return [];
  let codigos: string[] = [];
  let cantidad = 1;
  if (typeof pedido.cantidad === 'number' && pedido.cantidad > 0) {
    cantidad = pedido.cantidad;
  } else if (typeof pedido.CANTIDAD === 'number' && pedido.CANTIDAD > 0) {
    cantidad = pedido.CANTIDAD;
  } else if (typeof pedido.CANTIDAD === 'string' && !isNaN(Number(pedido.CANTIDAD))) {
    cantidad = Number(pedido.CANTIDAD);
  }
  if (Array.isArray(pedido.articulos)) {
    pedido.articulos.forEach((c: any) => {
      if (typeof c === 'string') {
        codigos.push(...c.split(',').map((x: string) => x.trim()).filter((x: string) => !!x));
      } else {
        codigos.push(String(c).trim());
      }
    });
  } else {
    codigos = String(pedido.articulos).split(',').map((c: string) => c.trim()).filter((c: string) => !!c);
  }
  const yaAgregadas = new Set((pedido.pruebasSolicitadas || []).map((pr: any) => pr.nombre));
  const nuevasPruebas: Array<{ nombre: string; ROW_ID: string; ACEPTADO: string }> = [];
  codigos.forEach((codigo: string) => {
    const nombre = CODIGO_A_NOMBRE_PRUEBA[codigo];
    if (nombre && !yaAgregadas.has(nombre)) {
      for (let i = 0; i < cantidad; i++) {
        nuevasPruebas.push({ nombre, ROW_ID: '', ACEPTADO: '' });
      }
    }
  });
  return [...(pedido.pruebasSolicitadas || []), ...nuevasPruebas];
}

interface UsePedidosReturn {
  pedidosState: Pedido[];
  setPedidosState: Dispatch<SetStateAction<Pedido[]>>;
  pruebasGuardadas: { [pedidoId: string]: { [prueba: string]: { row_id: string } } };
  setPruebasGuardadas: Dispatch<SetStateAction<{ [pedidoId: string]: { [prueba: string]: { row_id: string } } }>>;
}

export function usePedidos(
  pedidosProp: Pedido[],
  pruebasGuardadasProp: { [pedidoId: string]: { [prueba: string]: { row_id: string } } }
): UsePedidosReturn {
  const [pedidosState, setPedidosState] = useState<Pedido[]>(pedidosProp);
  const [pruebasGuardadas, setPruebasGuardadas] = useState<{ [pedidoId: string]: { [prueba: string]: { row_id: string } } }>(pruebasGuardadasProp);

  // Sincroniza pruebasGuardadas con el prop (backend)
  useEffect(() => {
    setPruebasGuardadas(pruebasGuardadasProp);
  }, [pruebasGuardadasProp]);

  // Sincroniza pruebasSolicitadas con pruebasGuardadas del backend
  // guardadas keys are `${nomId}__${pruebaNombre}` to support multiple pruebas of the same type
  useEffect(() => {
    if (!pedidosState.length) return;
    Object.entries(pruebasGuardadas).forEach(([pedidoId, guardadas]) => {
      setPedidosState(prev => prev.map(p => {
        if (p.id !== pedidoId) return p;
        const guardadasMap = Object.entries(guardadas).reduce((acc, [key, datos]) => {
          const rowId = (datos as any).row_id || '';
          acc[key] = {
            ...datos,
            ROW_ID: rowId,
            ACEPTADO: (typeof (datos as any).ACEPTADO === 'string' && (datos as any).ACEPTADO) || (typeof (datos as any).aceptado === 'string' && (datos as any).aceptado) || '',
          };
          return acc;
        }, {} as Record<string, any>);
        const nuevasPruebas = p.pruebasSolicitadas.map(prueba => {
          // Match by nomId__nombre (unique per nom record)
          const key = `${prueba.nomId}__${prueba.nombre}`;
          if (guardadasMap[key]) {
            return {
              ...prueba,
              ...guardadasMap[key],
              nombre: prueba.nombre,
              ROW_ID: guardadasMap[key].ROW_ID,
              ACEPTADO: guardadasMap[key].ACEPTADO,
              folioEmpresa: prueba.folioEmpresa,
            };
          }
          return prueba;
        });
        const iguales = JSON.stringify(p.pruebasSolicitadas) === JSON.stringify(nuevasPruebas);
        return iguales ? p : { ...p, pruebasSolicitadas: nuevasPruebas };
      }));
    });
  }, [pruebasGuardadas, pedidosState.length]);

  // Sincroniza pedidosState con pedidos del backend y aplica precarga de pruebas
  // Preserves local state (e.g. unsaved additions) while merging fresh backend data
  useEffect(() => {
    if (!pedidosProp || !Array.isArray(pedidosProp)) return;
    setPedidosState(prev => {
      const pedidosConPruebas = pedidosProp.map(p => ({
        ...p,
        pruebasSolicitadas: precargarPruebasDesdeArticulos(p),
      }));
      if (!prev.length) return pedidosConPruebas;
      // Merge: keep local additions (ROW_ID='') not yet in backend, use backend data otherwise
      return pedidosConPruebas.map(newP => {
        const oldP = prev.find(op => op.id === newP.id);
        if (!oldP) return newP;
        // Keep any locally-added pruebas with no ROW_ID that aren't in the new backend data
        const localOnly = oldP.pruebasSolicitadas.filter(
          pr => !pr.ROW_ID && !newP.pruebasSolicitadas.some(np => np.nomId === pr.nomId)
        );
        return localOnly.length
          ? { ...newP, pruebasSolicitadas: [...newP.pruebasSolicitadas, ...localOnly] }
          : newP;
      });
    });
  }, [pedidosProp]);

  return { pedidosState, setPedidosState, pruebasGuardadas, setPruebasGuardadas };
}
